/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useRef, Suspense } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import dynamic from 'next/dynamic'
import cn from 'classnames'

// Components
import Button from '@/components/newButton/Button'
import Loader from '@/components/newLoader/Loader'
import Header from '@/components/newHeader/Header'
import SearchBar from '@/components/search/Bar'
import PicCardMix from '@/components/picCardMix/picCardMix'
import Explore from '@/components/home/Explore'
import AllLocations from '@/components/allLocations'
import Footer from '@/components/newFooter'
import BasicHeadSeo from '@/components/basicHeadSeo'
// utils
import useMediaQuery from '@/utils/publicApi'
import { initializeApollo } from '@/utils/apolloClient'
import { GET_ALL_LOCATIONS, GET_EXPLORE_BREEDS, GET_CATEGORIES, PUPPY_SEARCH } from '@graphql'

import categoryPairs from '@/components/landings/util/arraySplit'
import styles from './index.module.scss'
import { ROBOTS_META } from 'const'

// Critical components - keep SSR enabled
const Categories = dynamic(() => import('@/components/home/Categories'), {
  ssr: true,
})

// Below-the-fold components - lazy load for better performance
const How = dynamic(() => import('@/components/home/How'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})
const Why = dynamic(() => import('@/components/home/Why'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})
const PuppyQuizBanner = dynamic(() => import('@/components/search/PuppyQuizBanner'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})
const AboutMawoo = dynamic(() => import('@/components/landings/AboutMawoo'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})

// Additional lazy loading for heavy components
const CustomerReview = dynamic(() => import('@/components/search/CustomerReview'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})
const OurPartners = dynamic(() => import('@/components/ourPartners'), {
  ssr: false,
  loading: () => (
    <div className={styles.loadingSection}>
      <Loader theme="short" />
    </div>
  ),
})

const LandingHero = ({ setNewRoute, isMobile, isTablet }) => {
  /* SEARCH BAR BEHAVIOR */
  const [pinSearchBar] = useState(false)
  const scrollRef = useRef(null)

  return (
    <div className={styles.landingHero}>
      <div className={styles.wrapper}>
        <div className={styles.heroTitlesContainer}>
          <div className={styles.titles}>
            <h2 className={styles.mainTitle}>Find your pup</h2>
            <h1 className={styles.subtitle}>Nationwide puppies for sale</h1>
            <h2>Our 1,000+ caring breeders have adorable puppies to introduce to you.</h2>
          </div>
          <div ref={scrollRef} className={styles.searchBar}>
            <SearchBar
              setSelectedBreed={() => {}}
              setShowNotificationForm={() => {}}
              isDiscountBannerClosed={false}
              isHome={false}
              setNewRoute={setNewRoute}
              scrollRef={scrollRef}
              pinSearchBar={pinSearchBar}
              isTablet={isTablet}
              // Only needed for mobile
              isMobile={isMobile}
            />
          </div>
          <a
            href="https://www.trustpilot.com/review/friendapuppy.com"
            className={styles.trustpilot}
          >
            <div className={styles.starts} />
            <div className={styles.logo} />
          </a>
        </div>
        <div className={styles.imageContainer}>
          <Image
            src="/landing/hero/landingHero_desktop1.5x.png"
            alt="Pomeranian, German Shepherd, and Pug dogs with a Green heart icon above their head"
            quality={75}
            layout="responsive"
            width={680.14}
            height={420.49}
            objectFit="contain"
            priority
          />
        </div>
      </div>
    </div>
  )
}

export default function index({ categoriesData, breedsExplorer, pickedPuppies, breedersLocation }) {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1194px)')

  const [isLoading, setIsLoading] = useState(false)

  const setNewRoute = (key, value) => {
    setIsLoading(true)

    const path = !value
      ? '/search'
      : `search?${key}=${value}${key === 'zipcode' ? '&sort=closest' : ''}`
    router
      .push(path)
      .then(() => window.scrollTo(0, 0))
      .then(() => setIsLoading(false))
  }

  return (
    <>
      <BasicHeadSeo
        title="Puppies for Sale - Browse 1,000+ Vetted Breeders Online | MawooPets.com"
        description="Browse adorable puppies for sale from the Mawoo Pets network of 1,000+ responsible & caring breeders across the United States & Canada. 4.9 stars + Nationwide Travel"
        url={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale`}
        image={`${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`}
        content={ROBOTS_META}
      />

      <div className={styles.main}>
        {isLoading && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />

        <LandingHero setNewRoute={setNewRoute} isTablet={isTablet} isMobile={isMobile} />

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="tall" />
            </div>
          }
        >
          <PicCardMix pickedPuppies={pickedPuppies} />
          <div className={styles.availableButton}>
            <Button
              title="View more"
              theme={['green-solid', 'medium']}
              rightImage="/next-white.svg"
              width={10}
              height={10}
              onClick={() => router.push('/search')}
            />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <div id="breed" className={styles.section}>
            <Explore title="Explore by Breed" breeds={breedsExplorer} hasSearch isLanding />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <div id="location" className={cn(styles.section, styles.locationDivider)}>
            <AllLocations breedersLocation={breedersLocation} hasSearch />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <div className={cn(styles.section, styles.categories)}>
            <Categories
              title="Explore by Category"
              categories={Object.keys(categoriesData?.categories).filter((c) => c !== '__typename')}
              isLanding
            />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <div className={styles.section}>
            <CustomerReview />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <How />
          <Why />
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <div className={styles.section}>
            <PuppyQuizBanner />
            <OurPartners />
          </div>
        </Suspense>

        <Suspense
          fallback={
            <div className={styles.loadingSection}>
              <Loader theme="short" />
            </div>
          }
        >
          <AboutMawoo />
        </Suspense>
        <Footer />
      </div>
    </>
  )
}

export async function getStaticProps() {
  const apolloClient = initializeApollo()

  try {
    const [featuredResult, locationsResult, explorerResult, categoryResult] = await Promise.all([
      apolloClient.query({
        query: PUPPY_SEARCH,
        variables: { sort: 'by-view-score', limit: 50 },
      }),
      apolloClient.query({
        query: GET_ALL_LOCATIONS,
      }),
      apolloClient.query({
        query: GET_EXPLORE_BREEDS,
      }),
      apolloClient.query({
        query: GET_CATEGORIES,
      }),
    ])

    const newFeatured = categoryPairs(featuredResult.data?.search?.rows) || []
    const locations = locationsResult.data
    const explorer = explorerResult.data
    const categoryData = categoryResult.data

    return {
      props: {
        categoriesData: categoryData || {},
        // Available puppies / Featured section
        pickedPuppies: newFeatured || [],
        // Explore by Breeds
        breedsExplorer: explorer?.breedsExplorer || {},
        // Explore by Location
        breedersLocation: locations || [],
      },
      revalidate: 60 * 60 * 24 * 7, // Revalidate 7 days
    }
  } catch (error) {
    console.error('PUPPIES-FOR_SALE INDEX', error)

    return {
      props: {
        categoriesData: {},
        pickedPuppies: [],
        breedsExplorer: {},
        breedersLocation: [],
      },
      revalidate: 60 * 60 * 24 * 7,
    }
  }
}
